import React from "react";
import { Spinner } from "../../../components";
import { useAdminUsageIterations } from "../../../api/hooks/admin";
import { WorkflowIterationRowDTO } from "../../../api/admin/types";
import { formatInt } from "../shared/formatters";

interface WorkflowIterationsProps {
  startDate: string;
  endDate: string;
  includeTestAccounts: boolean;
}

const BUCKET_COLORS: Record<string, string> = {
  "1": "#22c55e",   // green: one-turn = quality
  "2-3": "#84cc16", // lime
  "4-5": "#f59e0b", // amber
  "6+": "#ef4444",  // red: lots of iteration = friction
};

const BUCKET_KEYS = ["1", "2-3", "4-5", "6+"] as const;
type BucketKey = (typeof BUCKET_KEYS)[number];

const DistributionBar: React.FC<{ row: WorkflowIterationRowDTO }> = ({ row }) => {
  const total = row.chat_count || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "flex",
          width: "100%",
          height: 10,
          borderRadius: 4,
          overflow: "hidden",
          background: "#f3f4f6",
        }}
      >
        {BUCKET_KEYS.map((k) => {
          const v = row.distribution[k];
          if (v === 0) return null;
          const pct = (v / total) * 100;
          return (
            <div
              key={k}
              style={{
                width: `${pct}%`,
                background: BUCKET_COLORS[k],
              }}
              title={`${k} message${k === "1" ? "" : "s"}: ${v} chat${v === 1 ? "" : "s"}`}
            />
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: "#6b7280",
        }}
      >
        {BUCKET_KEYS.map((k) => (
          <span key={k}>
            <span style={{ display: "inline-block", width: 6, height: 6, background: BUCKET_COLORS[k], borderRadius: 1, marginRight: 4 }} />
            {k}: {row.distribution[k]}
          </span>
        ))}
      </div>
    </div>
  );
};

const IterationCard: React.FC<{ row: WorkflowIterationRowDTO }> = ({ row }) => {
  // One-turn% is the quality KPI - chats that the AI nailed without the user
  // having to nudge it. Colored on a simple traffic-light scale.
  const oneTurnColor =
    row.one_turn_pct >= 70 ? "#16a34a" : row.one_turn_pct >= 40 ? "#ca8a04" : "#dc2626";

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>{row.label}</div>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          {formatInt(row.chat_count)} chat{row.chat_count === 1 ? "" : "s"}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 700, color: oneTurnColor, lineHeight: 1 }}>
            {row.one_turn_pct}%
          </div>
          <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4 }}>
            delivered in ≤ 1 msg
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, marginLeft: "auto" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#374151" }}>{row.median}</div>
            <div style={{ fontSize: 10, color: "#9ca3af" }}>median</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#374151" }}>{row.p90}</div>
            <div style={{ fontSize: 10, color: "#9ca3af" }}>p90</div>
          </div>
        </div>
      </div>
      <DistributionBar row={row} />
    </div>
  );
};

const WorkflowIterations: React.FC<WorkflowIterationsProps> = ({
  startDate,
  endDate,
  includeTestAccounts,
}) => {
  const q = useAdminUsageIterations(
    startDate && endDate ? { startDate, endDate, includeTestAccounts } : null
  );

  const rows = q.data?.by_type ?? [];

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 20,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Workflow iterations</div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Human messages sent inside each workflow chat. Lower = the AI nailed the output
          with less back-and-forth.
        </div>
      </div>

      {q.isLoading ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#6b7280", padding: 24, textAlign: "center" }}>
          No workflow chats in this date range.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 12,
          }}
        >
          {rows.map((row) => (
            <IterationCard key={row.type} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowIterations;
